import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Globe, Monitor, QrCode, Settings2, Smartphone, Tablet, X, Zap } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { hasMessage } from '@cloudsign/shared';
import type { VisitAnnotation, VisitFlag, VisitMethod } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { Badge } from '../../components/ui';
import { FileUrl } from './api';
import { initialsOf } from './format';

/** Photo thumbnail when there is one (link from /files/:id/url), initials otherwise. */
export function PersonAvatar({ name, fileId, size = 'h-9 w-9 text-xs' }: { name: string; fileId: string | null; size?: string }) {
  const q = useQuery({
    queryKey: ['file-url', fileId],
    queryFn: () => api.get(`/files/${fileId}/url`, { schema: FileUrl }),
    enabled: Boolean(fileId),
    staleTime: 5 * 60_000,
  });
  if (fileId && q.data?.url) {
    return <img src={q.data.url} alt="" className={`${size} shrink-0 rounded-full object-cover ring-1 ring-slate-200`} />;
  }
  return (
    <span aria-hidden="true" className={`${size} inline-flex shrink-0 items-center justify-center rounded-full bg-brand-soft font-semibold text-slate-800 ring-1 ring-slate-200`}>
      {initialsOf(name)}
    </span>
  );
}

const METHOD_ICON: Record<VisitMethod, LucideIcon> = {
  kiosk: Tablet,
  portal: Monitor,
  companion: Smartphone,
  contactless: QrCode,
  point: Settings2,
  auto: Zap,
  api: Globe,
  system: Settings2,
};

/** Small icon with the method (and device name) as its accessible text. */
export function MethodIcon({ method, device }: { method: VisitMethod; device?: string | null }) {
  const Icon = METHOD_ICON[method];
  const label = device ? `${t(`today.method.${method}`)}: ${device}` : t(`today.method.${method}`);
  return (
    <span title={label} className="inline-flex items-center text-slate-500">
      <Icon aria-hidden="true" className="h-4 w-4" />
      <span className="sr-only">{label}</span>
    </span>
  );
}

const ANNOTATION_TONE = { info: 'neutral', warning: 'warn', danger: 'bad' } as const;

/** Markers other modules add to a visit (open permit, expired document, event...). The server sends an i18n key, not text. */
export function AnnotationBadges({ annotations }: { annotations: VisitAnnotation[] | undefined }) {
  if (!annotations || annotations.length === 0) return null;
  return (
    <>
      {annotations.map((a, i) => {
        const text = hasMessage(a.labelKey) ? t(a.labelKey, a.params ?? {}) : a.labelKey;
        return (
          <Badge key={`${a.kind}-${a.labelKey}-${i}`} tone={ANNOTATION_TONE[a.severity]}>
            {a.count && a.count > 1 ? `${text} (${a.count})` : text}
          </Badge>
        );
      })}
    </>
  );
}

export function FlagBadges({ flags }: { flags: VisitFlag[] }) {
  const shown = flags.filter((f) => f === 'offline' || f === 'clock_adjusted');
  if (shown.length === 0) return null;
  return (
    <>
      {shown.map((f) => (
        <Badge key={f} tone="warn">
          {t(`today.flag.${f}`)}
        </Badge>
      ))}
    </>
  );
}

/** Accessible overlay panel: a centred dialog or a right-hand drawer. Escape and the close button dismiss it. */
export function Overlay({
  title,
  onClose,
  children,
  footer,
  drawer = false,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  drawer?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [onClose]);
  return (
    <div className={`fixed inset-0 z-50 flex bg-slate-900/40 ${drawer ? 'justify-end' : 'items-center justify-center p-4'}`}>
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={
          drawer
            ? 'flex h-full w-full max-w-lg flex-col overflow-y-auto bg-white shadow-xl'
            : 'flex max-h-full w-full max-w-lg flex-col overflow-y-auto rounded-xl bg-white shadow-xl'
        }
      >
        <div className="flex items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100" aria-label={t('common.close')}>
            <X aria-hidden="true" className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 space-y-4 px-5 py-4">{children}</div>
        {footer ? <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">{footer}</div> : null}
      </div>
    </div>
  );
}
